// TaskDetailGPX — GP-portal view of a single task. Renders the Activity
// timeline (checklist UI handled separately). Layout aligns with the
// fund-admin-frontend ActivityTimeline at
// src/pages/Billing/CasesOverview/CaseDetailsDrawer/LogTab/ActivityTimeline.tsx
// — a single marker per row (check / xmark / empty circle, picked by action)
// threaded by a vertical line.

import React from "react";
import { Button, EmptyState, Flex, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { formatShortFormDate } from "#utils/datetime";
import { SPACING } from "#utils/spacing";
import * as styles from "./styles_vanilla.css";
import { TaskDeliverables } from "./TaskDeliverables";
import { getInitials, iconForAction, presentAction } from "./utils";
import type { ActivityEvent } from "../types";
import type { TaskDetailProps } from "./types";

// Pulls a string field from an ActivityEvent's free-form metaData blob without
// trusting the runtime shape — anything non-string is treated as absent.
const readStringMeta = (meta: ActivityEvent["metaData"], key: string): string | undefined => {
  const value = meta?.[key];
  return typeof value === "string" && value.length > 0 ? value : undefined;
};

// Safely reads the documents list from an external_documents_attached event's
// metaData. Returns only entries that have a non-empty string name.
const readAttachedDocuments = (
  meta: ActivityEvent["metaData"],
): { id: number; name: string }[] => {
  const raw = meta?.["documents"];
  if (!Array.isArray(raw)) return [];
  const result: { id: number; name: string }[] = [];
  for (const d of raw) {
    if (d === null || typeof d !== "object") continue;
    const entry = d as Record<string, unknown>;
    const id = entry["id"];
    const name = entry["name"];
    if (typeof id === "number" && typeof name === "string" && name.length > 0) {
      result.push({ id, name });
    }
  }
  return result;
};

type ActivityItemProps = { event: ActivityEvent; onShowComment?: (commentId: string) => void };

const ICON_COLOR_BY_TYPE = {
  "check-circle": Colors.Success500,
  "xmark-circle": Colors.Danger600,
  circle: Colors.Neutral500,
} as const;

const ICON_VARIANT_BY_TYPE = {
  "check-circle": "solid",
  "xmark-circle": "solid",
  circle: "outline",
} as const;

const ActivityItem: React.FC<ActivityItemProps> = ({ event, onShowComment }) => {
  const { label, preposition } = presentAction(event.action);
  const actorName = event.actorName ?? "Unknown";
  const iconType = iconForAction(event.action);

  const commentId =
    event.action === "bts_note_added"
      ? readStringMeta(event.metaData, "comment_id")
      : undefined;
  const showCommentLink = !!commentId && !!onShowComment;

  const attachedDocuments =
    event.action === "external_documents_attached" ? readAttachedDocuments(event.metaData) : [];

  return (
    <Flex flexDirection="column" gap={SPACING.X_SMALL} className={styles.activityItem}>
      <Icon
        type={iconType}
        size="small"
        color={ICON_COLOR_BY_TYPE[iconType]}
        variant={ICON_VARIANT_BY_TYPE[iconType]}
        className={styles.activityIcon}
      />
      <Flex alignItems="center" gap={SPACING.X_SMALL} flexWrap="wrap">
        <Text variant="regular-md" color={Colors.Neutral800}>
          {label} {preposition}
        </Text>
        <Flex alignItems="center" justifyContent="center" className={styles.activityAvatar}>
          {getInitials(actorName)}
        </Flex>
        <Text variant="regular-md" color={Colors.Neutral800}>
          {actorName}
        </Text>
      </Flex>
      {attachedDocuments.length > 0 && (
        <Flex flexDirection="column" gap={SPACING.X_SMALL}>
          {attachedDocuments.map((doc) => (
            <Text
              key={doc.id}
              variant="regular-sm"
              color={Colors.Neutral600}
              data-testid={`activity-document-${event.id}-${doc.id}`}
            >
              {doc.name}
            </Text>
          ))}
        </Flex>
      )}
      <Flex alignItems="center" gap={SPACING.SMALL} flexWrap="wrap">
        <Text variant="regular-sm" color={Colors.Neutral500}>
          {formatShortFormDate(event.occurredAt)}
        </Text>
        {showCommentLink && commentId && (
          <Button
            variant="neutral"
            displayAsLink
            onClick={() => onShowComment?.(commentId)}
            data-testid={`activity-see-comment-${event.id}`}
            className={styles.seeCommentButton}
          >
            See comment
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export const TaskDetailGPX: React.FC<TaskDetailProps> = ({
  task,
  onBack,
  deliverables = [],
  onShowComment,
}) => {
  const events = task.activityLog;
  const isActivityListEmpty = events.length === 0;

  const Header = <Text variant="h3">Activity</Text>;
  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM} className={styles.taskDetailContainer}>
      <div className={styles.taskDetailCard}>
        {/* Back link */}
        <button
          type="button"
          className={styles.backLink}
          onClick={onBack}
          data-testid="task-detail-back"
        >
          <Icon type="arrow-left" size="small" color={Colors.Neutral600} />
          All tasks
        </button>
        <Flex
          flexDirection="column"
          gap={SPACING.MEDIUM}
          className={styles.activityCard}
          data-testid="task-detail-gpx-activity"
        >
          {isActivityListEmpty ? (
            <Flex justifyContent="center" className={styles.activityHeaderEmptyCase}>
              {Header}
            </Flex>
          ) : (
            Header
          )}
          {isActivityListEmpty ? (
            <EmptyState
              illustration="document-locked"
              description="No activity yet"
              data-testid="task-detail-gpx-activity-empty"
            />
          ) : (
            <Flex flexDirection="column" className={styles.activityList}>
              {events.map((event) => (
                <ActivityItem key={event.id} event={event} onShowComment={onShowComment} />
              ))}
            </Flex>
          )}
        </Flex>
        <TaskDeliverables
          deliverables={deliverables}
          subtasks={[...task.subtasks, ...(task.mirroredSubtasks ?? [])]}
        />
      </div>
    </Flex>
  );
};

export default TaskDetailGPX;
